var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import styled, { css, useTheme, } from 'styled-components';
import { margin, compose } from 'styled-system';
import { v4 as uuid } from 'uuid';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';
import { useButtonWidth, useDerivedSize } from './hooks';
import { addAllyTM } from '../tools/utils';
import { buttonVariants, getIconMargin, mapHexToColorName, stylesBySize, } from './utils';
import { muiBankTheme } from '../tools/theme';
import { useLoading } from './hooks/useLoading';
import { LoadingBar } from './LoadingBar';
import { a11y } from './strings';
var styledButtonConfig = {
    /**
     * This is necessary because styled components will not pass
     * non-standard attributes to DOM elements if `styled` is wrapping
     * a native DOM element. However, if a custom component is wrapped
     * by `styled`, then all attributes/props will be passed. See
     * https://styled-components.com/docs/basics#passed-props
     */
    shouldForwardProp: function (prop, defaultValidatorFn) {
        return prop === 'allytmln' || defaultValidatorFn(prop);
    },
};
var StyledButton = styled.button.withConfig(styledButtonConfig)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: relative;\n  overflow: hidden;\n  z-index: 0;\n  ", "\n\n  width: ", ";\n  transition: box-shadow 0.15s ease, background 0.15s ease;\n  box-sizing: border-box;\n  ", "\n\n  ", ";\n"], ["\n  position: relative;\n  overflow: hidden;\n  z-index: 0;\n  ", "\n\n  width: ", ";\n  transition: box-shadow 0.15s ease, background 0.15s ease;\n  box-sizing: border-box;\n  ", "\n\n  ", ";\n"])), compose(margin, buttonVariants), function (_a) {
    var width = _a.width;
    return width;
}, function (_a) {
    var variant = _a.variant;
    return (variant !== 'link' &&
        variant !== 'disabled' && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        &:hover {\n          box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.16);\n          box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.08),\n            0 3px 1px -2px rgba(0, 0, 0, 0.08), 0 1px 5px 0 rgba(0, 0, 0, 0.16);\n        }\n      "], ["\n        &:hover {\n          box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.16);\n          box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.08),\n            0 3px 1px -2px rgba(0, 0, 0, 0.08), 0 1px 5px 0 rgba(0, 0, 0, 0.16);\n        }\n      "]))));
}, function (props) {
    return props.variant !== 'link' && props.size ? stylesBySize[props.size] : css(templateObject_2 || (templateObject_2 = __makeTemplateObject([""], [""])));
});
var StyledContentWrapper = styled.span(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  opacity: ", ";\n\n  z-index: 1;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  opacity: ", ";\n\n  z-index: 1;\n"])), function (_a) {
    var isLoading = _a.isLoading, isLinkVariant = _a.isLinkVariant;
    return isLoading && isLinkVariant ? '0.0' : '1.0';
});
var StyledSpinnerWrapper = styled.span(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  position: absolute;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  top: 0;\n  bottom: 0;\n  right: 0;\n  left: 0;\n  z-index: 1;\n"], ["\n  position: absolute;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  top: 0;\n  bottom: 0;\n  right: 0;\n  left: 0;\n  z-index: 1;\n"])));
var defaultProps = {
    type: 'button',
    isLoading: false,
    variant: 'primary',
    disableFullWidthSmDown: false,
    fullWidthMd: false,
    disabled: false,
    iconPosition: 'left',
};
var getIconProps = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, content = _a.content;
    return (__assign({ icon: icon.map, size: size, fill: iconFill, ariaHidden: true, customTitle: icon.customTitle }, (content ? getIconMargin(theme, icon === null || icon === void 0 ? void 0 : icon.position) : {})));
};
var IconWrapper = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, children = _a.children;
    var iconProps = getIconProps({
        icon: icon,
        iconFill: iconFill,
        theme: theme,
        size: size,
        content: children,
    });
    if (icon.position === 'right') {
        return (React.createElement(React.Fragment, null,
            children,
            React.createElement(Icon, __assign({}, iconProps))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Icon, __assign({}, iconProps)),
        children));
};
var IconWithText = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, children = _a.children;
    return (icon === null || icon === void 0 ? void 0 : icon.map) ? (React.createElement(IconWrapper, { icon: icon, iconFill: iconFill, theme: theme, size: size }, children)) : (React.createElement(React.Fragment, null, children));
};
var Button = React.forwardRef(function (props, ref) {
    var _a;
    var text = props.text, type = props.type, className = props.className, isLoading = props.isLoading, loading = props.loading, disableFullWidthSmDown = props.disableFullWidthSmDown, fullWidthMd = props.fullWidthMd, ariaLabel = props.ariaLabel, _b = props.withAllyTM, withAllyTM = _b === void 0 ? true : _b, id = props.id, onClick = props.onClick, icon = props.icon, _c = props.ariaLiveLoading, ariaLiveLoading = _c === void 0 ? a11y.ariaLiveLoading : _c, unsupportedContent = props.unsupportedContent, others = __rest(props, ["text", "type", "className", "isLoading", "loading", "disableFullWidthSmDown", "fullWidthMd", "ariaLabel", "withAllyTM", "id", "onClick", "icon", "ariaLiveLoading", "unsupportedContent"]);
    var btnUUID = React.useRef(uuid()).current;
    var safeVariant = props.variant || defaultProps.variant;
    var safeId = id || "".concat(safeVariant, "-button-").concat(btnUUID);
    var allyTM = addAllyTM({
        content: text,
        id: safeId,
        elementType: 'interactive',
        withAllyTM: withAllyTM,
    });
    var derivedSize = useDerivedSize(safeVariant, props.variant !== 'link' ? props.size : undefined);
    var width = useButtonWidth(safeVariant, !!disableFullWidthSmDown, !!fullWidthMd, derivedSize).width;
    var loaderTheme = useMemo(function () {
        var sv = safeVariant;
        return sv === 'primary' || sv === 'login' || sv === 'destructive'
            ? 'light'
            : 'dark';
    }, [safeVariant]);
    // Theme provides the color hex value, but Icon needs the color name
    // So we find the color name that maps to the hex value
    var iconFill = useMemo(function () { var _a; return mapHexToColorName((_a = muiBankTheme.buttons[safeVariant]) === null || _a === void 0 ? void 0 : _a.color); }, [safeVariant]);
    var theme = useTheme();
    // TODO REMOVE alias "_isLoading ||" FOR METRONOME V23
    var _d = useLoading(ariaLiveLoading, typeof loading === 'number' ? loading : isLoading || loading), _isLoading = _d.isLoading, loadingPercentage = _d.loadingPercentage;
    return (React.createElement(StyledButton, __assign({ ref: ref, type: type, "aria-label": ariaLabel, className: className, width: width, id: safeId }, (props.variant !== 'link' ? { size: derivedSize } : {}), { "aria-busy": _isLoading, 
        // TODO REMOVE alias "_isLoading ||" FOR METRONOME V23
        onClick: _isLoading ? undefined : onClick, "aria-disabled": _isLoading }, allyTM, others),
        props.variant !== 'link' && _isLoading && (React.createElement(LoadingBar, { variant: (_a = props.variant) !== null && _a !== void 0 ? _a : 'primary', loading: 
            // TODO REMOVE alias "_isLoading ||" FOR METRONOME V23
            typeof loadingPercentage === 'number'
                ? loadingPercentage
                : _isLoading })),
        React.createElement(StyledContentWrapper, { isLoading: _isLoading, isLinkVariant: props.variant === 'link' },
            React.createElement(IconWithText, __assign({ icon: icon, iconFill: iconFill, theme: theme }, (props.variant !== 'link' ? { size: props.size } : {})), text || unsupportedContent)),
        props.variant === 'link' && _isLoading && (React.createElement(StyledSpinnerWrapper, null,
            React.createElement(Spinner, { size: "small", themeColor: loaderTheme })))));
});
Button.defaultProps = defaultProps;
export { Button };
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
//# sourceMappingURL=Button.js.map